import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { useSnackbar } from 'notistack';
import {
  pushAlert,
  closeAlert,
  clearAlerts,
} from "../features/alerts/alertsSlice";
import { selectAlerts } from "../features/alerts/alertsSelectors";
import { AlertMessage } from "../features/alerts/alertsTypes";

const getSeverityFromStatus = (status: number): 'success' | 'error' | 'warning' | 'info' => {
  if (status === 200) return 'success';
  if (status === 500 || status >= 400) return 'error';
  if (status === 100) return 'info';
  if (status >= 300) return 'warning';
  return 'info';
};

export const useAlerts = () => {
  const dispatch = useAppDispatch();
  const alerts = useAppSelector(selectAlerts);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const pushAlertMessage = useCallback(
    (alert: AlertMessage) => {
      dispatch(pushAlert(alert));

      // Also show via notistack
      const variant = getSeverityFromStatus(alert.status);
      enqueueSnackbar(alert.message, {
        variant,
        autoHideDuration: alert.autoCloseDelay || 5000,
        key: alert.id || undefined,
      });
    },
    [dispatch, enqueueSnackbar]
  );

  const closeAlertMessage = useCallback(
    (id: string) => {
      dispatch(closeAlert(id));
      closeSnackbar(id);
    },
    [dispatch, closeSnackbar]
  );

  const clearAlertsMessages = useCallback(() => {
    dispatch(clearAlerts());
  }, [dispatch]);

  const showSuccess = useCallback(
    (message: string, options?: Partial<AlertMessage>) => {
      enqueueSnackbar(message, {
        variant: 'success',
        autoHideDuration: options?.autoCloseDelay || 5000,
      });

      dispatch(
        pushAlert({
          message,
          status: 200,
          ...options,
        })
      );
    },
    [dispatch, enqueueSnackbar]
  );

  const showError = useCallback(
    (
      message: string,
      status: number = 500,
      options?: Partial<AlertMessage>
    ) => {
      enqueueSnackbar(message, {
        variant: 'error',
        autoHideDuration: options?.autoCloseDelay || 5000,
      });

      dispatch(
        pushAlert({
          message,
          status,
          ...options,
        })
      );
    },
    [dispatch, enqueueSnackbar]
  );

  const showInfo = useCallback(
    (message: string, options?: Partial<AlertMessage>) => {
      enqueueSnackbar(message, {
        variant: 'info',
        autoHideDuration: options?.autoCloseDelay || 5000,
      });

      dispatch(
        pushAlert({
          message,
          status: 100,
          ...options,
        })
      );
    },
    [dispatch, enqueueSnackbar]
  );

  return {
    alerts,
    pushAlertMessage,
    closeAlertMessage,
    clearAlertsMessages,
    showSuccess,
    showError,
    showInfo,
  };
};